import { useEffect, useState } from "react";
import type { PersonalTaskInput } from "../../hooks/usePersonalTasks";

export interface AddToMyListDialogProps {
  initial: {
    subject: string;
    description?: string;
  };
  onSave: (input: PersonalTaskInput) => Promise<void>;
  onCancel: () => void;
}

/** "YYYY-MM-DD" today in Pacific time. */
function todayISO(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
  }).format(new Date());
}

/**
 * Lightweight modal for adding a Watchtower alert to MY LIST. Three fields
 * (subject, description, due date) — no priority, no deal picker, since
 * personal tasks don't carry those concepts at this surface.
 */
export function AddToMyListDialog({
  initial,
  onSave,
  onCancel,
}: AddToMyListDialogProps) {
  const [subject, setSubject] = useState(initial.subject);
  const [description, setDescription] = useState(initial.description ?? "");
  const [dueDate, setDueDate] = useState(todayISO());
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const canSave = subject.trim().length > 0 && !running;

  const handleSave = async () => {
    const trimmed = subject.trim();
    if (!trimmed) {
      setError("subject_required");
      return;
    }
    setError(null);
    setRunning(true);
    try {
      await onSave({
        subject: trimmed,
        description: description.trim() || null,
        due_date: dueDate || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "save_failed");
      setRunning(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card mylist-add-card">
        <div className="write-confirm-title">Add to MY LIST</div>
        <div className="crm-create-body">
          <label className="crm-create-field">
            <span className="crm-create-label">Subject</span>
            <input
              className="crm-create-input"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              autoFocus
            />
          </label>
          <label className="crm-create-field">
            <span className="crm-create-label">Description</span>
            <textarea
              className="crm-create-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </label>
          <label className="crm-create-field">
            <span className="crm-create-label">Due date</span>
            <input
              className="crm-create-input"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </label>
        </div>
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleSave}
            disabled={!canSave}
          >
            {running ? "ADDING…" : "ADD TO MY LIST"}
          </button>
        </div>
      </div>
    </div>
  );
}
